import { useState, useCallback, useRef, useEffect } from 'react';
import { fetchTransaction } from '../../api';
import type { Transaction } from '../../types';

interface UseTransactionStatusPollingOptions {
  transactionId: number | null;
  onConfirmed?: (transaction: Transaction) => void;
  onTimeout?: () => void;
  onError?: (error: unknown) => void;
  pollingIntervalMs?: number;
  maxAttempts?: number;
}

interface UseTransactionStatusPollingResult {
  isPolling: boolean;
  startPolling: () => void;
}

export function useTransactionStatusPolling({
  transactionId,
  onConfirmed,
  onTimeout,
  onError,
  pollingIntervalMs = 1500,
  maxAttempts = 7,
}: UseTransactionStatusPollingOptions): UseTransactionStatusPollingResult {
  const [isPolling, setIsPollingState] = useState(false);
  const abortRef = useRef(false);

  const isPollingRef = useRef(isPolling);
  const transactionIdRef = useRef(transactionId);
  const onConfirmedRef = useRef(onConfirmed);
  const onTimeoutRef = useRef(onTimeout);
  const onErrorRef = useRef(onError);

  const setIsPolling = useCallback((value: boolean) => {
    isPollingRef.current = value;
    setIsPollingState(value);
  }, []);

  useEffect(() => {
    transactionIdRef.current = transactionId;
    onConfirmedRef.current = onConfirmed;
    onTimeoutRef.current = onTimeout;
    onErrorRef.current = onError;
  });

  useEffect(() => {
    return () => {
      abortRef.current = true;
    };
  }, []);

  const startPolling = useCallback(() => {
    if (isPollingRef.current || transactionIdRef.current == null) return;

    setIsPolling(true);
    abortRef.current = false;

    const poll = async () => {
      for (let i = 0; i < maxAttempts; i++) {
        if (abortRef.current) return;

        await new Promise((r) => setTimeout(r, pollingIntervalMs));

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- ref can change during async
        if (abortRef.current) return;

        if (transactionIdRef.current == null) return;

        let transaction;
        try {
          transaction = await fetchTransaction(transactionIdRef.current);
        } catch (error) {
          setIsPolling(false);
          onErrorRef.current?.(error);
          return;
        }

        if (transaction.status === 'posted') {
          setIsPolling(false);
          onConfirmedRef.current?.(transaction);
          return;
        }
      }

      if (abortRef.current) return;

      setIsPolling(false);
      onTimeoutRef.current?.();
    };

    void poll();
  }, [setIsPolling, pollingIntervalMs, maxAttempts]);

  return {
    isPolling,
    startPolling,
  };
}
